import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, Type } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import {
  Booking,
  BOOKING_TRANSFORMATION_SERVICE,
  BookingIncidentType,
  BookingTransformationService,
} from '@luggagehero/shared/interfaces';
import { SharedBookingService } from '@luggagehero/shared/services/bookings';
import { SharedLocationService } from '@luggagehero/shared/services/locations';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedStorageCriteria, SharedStorageCriteriaService } from '@luggagehero/shared/services/storage-criteria';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { filter, Subscription } from 'rxjs';

import { BaseComponent, ModalStyle } from '../../../../core';
import { ModalService } from '../../../../core/services/index';
import { BookingActionInfo } from '../../../bookings';
import { CheckInActionBaseComponent } from './booking-actions/check-in-action.base-component';
import { CheckOutActionBaseComponent } from './booking-actions/check-out-action.base-component';

type BookingEventType = 'system';

interface BookingEvent {
  actor: string;
  date: Date;
  text: string;
  type: BookingEventType;
}

@Component({ template: '' })
export abstract class StorageLocationBookingBaseComponent extends BaseComponent implements OnInit, OnDestroy {
  public booking: Booking;
  public bookingId: string;
  public storageLocationId: string;
  public error = false;
  public bookingEvents: BookingEvent[];

  protected modalService = inject(ModalService);
  private bookingTransformationService = inject<BookingTransformationService>(BOOKING_TRANSFORMATION_SERVICE);
  private criteriaService = inject(SharedStorageCriteriaService);
  private locationService = inject(SharedLocationService);

  private subscriptions: Subscription[];
  private _isCheckInSession = false;
  private _isSmallScreen = false;
  private _isLoading = false;

  constructor(
    private bookingService: SharedBookingService,
    private notify: SharedNotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private window: SharedWindowService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  public get currentAction(): BookingActionInfo {
    if (!AppConfig.IS_STORAGE_MANAGER_BOOKING_ACTIONS_ENABLED) {
      return null;
    }
    switch (this.booking?.status) {
      case 'CONFIRMED':
        return new BookingActionInfo('CheckInFromStorageManagerBookingDetails')
          .withCallToAction('CHECK_IN')
          .withAction({ componentToShow: this.checkInActionComponentType });

      case 'CHECKED_IN':
      case 'CHECKED_OUT':
        return new BookingActionInfo('CheckOutFromStorageManagerBookingDetails')
          .withCallToAction(this.booking.status === 'CHECKED_OUT' ? 'RETRY_PAYMENT_BUTTON' : 'CHECK_OUT')
          .withAction({ componentToShow: this.checkOutActionComponentType });

      default:
        return null;
    }
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }
  public set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  public get isCheckInSession(): boolean {
    return this._isCheckInSession;
  }
  public set isCheckInSession(value: boolean) {
    this._isCheckInSession = value;
    this.cd.markForCheck();
  }

  public get isSmallScreen(): boolean {
    return this._isSmallScreen;
  }

  public get isBookingAmountVisible(): boolean {
    if (!AppConfig.IS_STORAGE_MANAGER_BOOKING_AMOUNT_ENABLED) {
      return false;
    }

    if (!this.bookingAmount) {
      return false;
    }

    return true;
  }

  public get bookingAmountTooltip(): string {
    return this.isDirectPaymentRequired ? 'YOU_NEED_TO_CHARGE_THIS_AMOUNT' : 'YOUR_NET_EARNINGS_FOR_THIS_BOOKING';
  }

  public get bookingAmount(): number {
    if (!this.booking || !['CHECKED_OUT', 'PAID'].includes(this.booking.status)) {
      return null;
    }
    return this.isDirectPaymentRequired ? this.booking.fullBookingAmount : this.booking.storageLocationBookingAmount;
  }

  public get currency(): string {
    if (!this.booking) {
      return null;
    }
    if (this.booking.storageLocationRevenuePlus && !this.isDirectPaymentRequired) {
      return this.booking.storageLocationRevenuePlus.currency;
    }
    return this.booking.price.final?.currency || this.booking.price.pricing.currency;
  }

  public get isDirectPaymentRequired(): boolean {
    if (!this.booking) {
      return false;
    }
    return this.booking.status === 'CHECKED_OUT' || (this.booking.status === 'PAID' && this.booking.paidDirectly);
  }

  public get isCheckedIn(): boolean {
    if (!this.booking) {
      return false;
    }
    return this.booking.status === 'CHECKED_IN';
  }

  public get isCheckinToday(): boolean {
    if (!this.booking) {
      return false;
    }
    return moment(this.booking.period.checkIn).isSame(new Date(), 'day');
  }

  public get isCheckoutToday(): boolean {
    if (!this.booking) {
      return false;
    }
    return moment(this.booking.period.checkOut).isSame(new Date(), 'day');
  }

  public get showContactDetails(): boolean {
    if (!this.booking) {
      return false;
    }
    return this.booking.status === 'CHECKED_IN' || this.isCheckinToday || this.isCheckoutToday;
  }

  public get userName(): string {
    return this.bookingTransformationService.transformBooking(this.booking, 'user');
  }

  public get numberOfBags(): number {
    if (!this.booking?.luggage) {
      return 0;
    }
    return this.booking.luggage.normal + this.booking.luggage.hand;
  }

  protected abstract get checkInActionComponentType(): Type<CheckInActionBaseComponent>;
  protected abstract get checkOutActionComponentType(): Type<CheckOutActionBaseComponent>;

  public ngOnInit() {
    this.subscriptions = [];
    this.subscriptions.push(
      this.route.params.subscribe((params: { bookingId: string; id: string }) => {
        return void (async () => {
          // HACK: This is a workaround for the issue where the translate service is not ready when the component is
          await new Promise<void>((resolve) => this.translate.get('TEST').subscribe(() => resolve()));

          // Store booking and storage location IDs from URL params
          this.bookingId = params['bookingId'];
          this.storageLocationId = params['id'];

          await this.loadBooking();
        })();
      }),
    );
    this.subscriptions.push(
      this.window.size.subscribe((value) => {
        this._isSmallScreen = value.sizeGroup === 'xs';
        this.cd.markForCheck();
      }),
    );
    this.subscriptions.push(
      this.bookingService.bookingEvent$
        .pipe(filter((e) => e?.eventType === 'bagsDroppedOff'))
        .subscribe((_e) => (this.isCheckInSession = true)),
    );
    this.subscriptions.push(this.bookingService.currentBookingChange$.subscribe((b) => void this.onBookingChange(b)));
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public goBackToBookingList() {
    if (
      this.window.previousUrl &&
      this.window.previousUrl.startsWith(`/storage-locations/${this.storageLocationId}/bookings`)
    ) {
      this.window.back();
    } else {
      void this.router.navigate(['/storage-locations', this.storageLocationId, 'bookings']);
    }
  }

  public async loadBooking(refresh = false): Promise<void> {
    this.isLoading = true;

    let minDelay: Promise<void>;
    if (refresh) {
      minDelay = new Promise((resolve) => setTimeout(resolve, 2000));
    }

    try {
      const res = await this.bookingService.getBookingForStorageLocation(
        this.storageLocationId,
        this.bookingId,
        refresh,
      );
      void this.onBookingChange(res);
      this.error = false;
    } catch (err) {
      this.notify.error(this.translate.instant('BOOKING_COULD_NOT_BE_LOADED') as string);
      this.error = true;
    }

    if (minDelay != null) {
      await minDelay;
    }

    if (refresh && !this.error) {
      this.notify.chat(this._translate('BOOKING_DATA_REFRESH_BODY'), this._translate('BOOKING_DATA_REFRESH_HEADER'));
    }

    this.isLoading = false;
  }

  public requestAction(value: BookingActionInfo) {
    if (!value?.action) {
      return;
    }

    if (value.action.componentToShow) {
      void this.modalService.show({
        component: value.action.componentToShow,
        componentState: {
          booking: this.booking,
        },
        style: ModalStyle.FullScreen,
      });
    }

    if (typeof value.action.functionToCall === 'function') {
      void value.action.functionToCall();
    }
  }

  private async onBookingChange(value: Booking) {
    this.booking = value;
    this.buildBookingEvents();

    if (!this.locationService.isInitialized) {
      await this.locationService.init();
    }
    this.criteriaService.current = new SharedStorageCriteria(
      this.locationService.nearestOfficialLocation,
      this.booking.period,
      this.booking.luggage,
    );
  }

  private buildBookingEvents() {
    if (!this.booking || this.error) {
      return;
    }
    this.bookingEvents = [];

    const userName = this.bookingTransformationService.transformBooking(this.booking, 'user');
    const isWalkIn =
      this.booking.price.addOns?.walkIn || this.booking.metadata?.lh_booking_category === 'walk_in' || false;

    if (!isWalkIn) {
      this.bookingEvents.push({
        actor: userName,
        date: this.booking.created,
        text: 'CONFIRMED_BOOKING',
        type: 'system',
      });
    }

    if (this.booking.incidentReported) {
      for (const incident of this.booking.incidents) {
        this.bookingEvents.push({
          actor: userName,
          date: incident.reportedAt,
          text: this.getIncidentText(incident.incidentType),
          type: 'system',
        });
      }
    }

    if (['CHECKED_IN', 'CHECKED_OUT', 'PAID'].includes(this.booking.status)) {
      const textBefore = this.translate.instant('DROPPED_OFF') as string;
      const numberOfBags = this.booking.luggage.normal + this.booking.luggage.hand;
      const textAfter = this.translate.instant(this.booking.luggage.normal === 1 ? 'BAG' : 'BAGS') as string;

      this.bookingEvents.push({
        actor: userName,
        date: this.booking.period.checkIn,
        text: `${textBefore} ${numberOfBags} ${textAfter}`,
        type: 'system',
      });
    }

    switch (this.booking.status) {
      case 'PAID': {
        let text = 'COMPLETED_PAYMENT_ONLINE';
        if (this.booking.paidDirectly) {
          text = 'FAILED_TO_COMPLETE_PAYMENT';
        } else if (this.booking.isZeroPaymentBooking) {
          const numberOfBags = this.booking.luggage.normal + this.booking.luggage.hand;
          const textAfter = this.translate.instant(this.booking.luggage.normal === 1 ? 'BAG' : 'BAGS') as string;
          text = `${this.translate.instant('COMPLETED_ZERO_PAYMENT_BOOKING')} ${numberOfBags} ${textAfter}`;
        }
        this.bookingEvents.push({
          actor: userName,
          date: this.booking.period.checkOut,
          text: text,
          type: 'system',
        });
        break;
      }
      case 'CANCELLED': {
        this.bookingEvents.push({
          actor: userName,
          date: new Date(this.booking.orderDetails[0].createdAt),
          text: 'CANCELLED_BOOKING',
          type: 'system',
        });
        break;
      }
    }

    this.bookingEvents.sort((a, b) => b.date.getTime() - a.date.getTime());
    this.cd.markForCheck();
  }

  private getIncidentText(incidentType: BookingIncidentType): string {
    switch (incidentType) {
      case BookingIncidentType.AskedToPayDirectly:
        return 'REPORTED_INCIDENT_ASKED_TO_PAY_DIRECTLY';

      case BookingIncidentType.LocationClosedOnArrival:
        return 'REPORTED_INCIDENT_LOCATION_CLOSED_ON_ARRIVAL';

      case BookingIncidentType.LocationRefusedBags:
        return 'REPORTED_INCIDENT_LOCATION_REFUSED_BAGS';

      case BookingIncidentType.StaffUnawareOfService:
        return 'REPORTED_INCIDENT_STAFF_UNAWARE_OF_SERVICE';

      case BookingIncidentType.UnableToFindLocation:
        return 'REPORTED_INCIDENT_UNABLE_TO_FIND_LOCATION';

      default:
        // This shouldn't happen
        return 'REPORTED_AN_INCIDENT';
    }
  }
}
